import createPersistedState from 'vuex-persistedstate'

import { isOrgSwitchHandoffActive } from '@/lib/orgSwitchHandoff'

const PERSISTENCE_KEY = 'rever-vuex-state-v1'

const HOME_SNAPSHOTS_PERSISTENCE_KEY = 'rever-home-snapshots-v1'

const PERSISTED_PATHS = Object.freeze([
  'access.account',
  'access.userId',
  'access.reverScore',
  'access.organization',
  'access.organizationId',
  'access.siteId',
  'access.logoMediaObject',
  'access.tags',
  'organization.organizationStructure',
  'language',
  'currency',
])

const PERSISTED_MUTATION_TYPES = Object.freeze({
  SET_ACCOUNT: 'SET_ACCOUNT',
  SET_USER_ID: 'SET_USER_ID',
  SET_ORGANIZATION_ID: 'SET_ORGANIZATION_ID',
  SET_SITE_ID: 'SET_SITE_ID',
  SET_ORGANIZATION: 'SET_ORGANIZATION',
  SET_TAGS: 'SET_TAGS',
  SET_LOGO: 'SET_LOGO',
  SET_ACCOUNT_POINTS: 'SET_ACCOUNT_POINTS',
  SET_ORGANIZATION_AI_SETTINGS: 'SET_ORGANIZATION_AI_SETTINGS',
  UPDATE_ACCOUNT_LOCATION: 'UPDATE_ACCOUNT_LOCATION',
  ACCEPT_TERMS_OF_SERVICE: 'ACCEPT_TERMS_OF_SERVICE',
  SET_ORGANIZATION_STRUCTURE: 'SET_ORGANIZATION_STRUCTURE',
  LANGUAGE: 'LANGUAGE',
  SET_ORGANIZATION_CURRENCY: 'SET_ORGANIZATION_CURRENCY',
})

const HOME_SNAPSHOT_PERSISTED_PATHS = Object.freeze([
  'homeSnapshots.toDo',
  'homeSnapshots.routines',
  'homeSnapshots.activityBoard',
])

const HOME_SNAPSHOT_PERSISTED_MUTATION_TYPES = Object.freeze({
  'homeSnapshots/SET_TO_DO_SNAPSHOT': 'homeSnapshots/SET_TO_DO_SNAPSHOT',
  'homeSnapshots/SET_ROUTINES_SNAPSHOT': 'homeSnapshots/SET_ROUTINES_SNAPSHOT',
  'homeSnapshots/SET_ACTIVITY_BOARD_SNAPSHOT':
    'homeSnapshots/SET_ACTIVITY_BOARD_SNAPSHOT',
})

function createSafeStorage() {
  const safeOperation = (operation, fallback) => {
    try {
      return operation()
    } catch (error) {
      console.error('Error persisting store state:', error)
      return fallback
    }
  }

  return {
    getItem: key =>
      safeOperation(() => window.localStorage.getItem(key), undefined),
    setItem: (key, value) =>
      safeOperation(() => window.localStorage.setItem(key, value), undefined),
    removeItem: key =>
      safeOperation(() => window.localStorage.removeItem(key), undefined),
  }
}

function isPersistedMutation(persistedTypes, mutation) {
  return Boolean(persistedTypes[mutation?.type])
}

function removePersistedOrgSession() {
  try {
    window.localStorage.removeItem(HOME_SNAPSHOTS_PERSISTENCE_KEY)

    const raw = window.localStorage.getItem(PERSISTENCE_KEY)
    if (!raw) return

    const persistedState = JSON.parse(raw)
    if (!persistedState) return

    delete persistedState.access
    delete persistedState.organization

    window.localStorage.setItem(PERSISTENCE_KEY, JSON.stringify(persistedState))
  } catch (error) {
    console.error(
      'Error stripping the persisted org snapshot, removing it entirely:',
      error
    )
    window.localStorage.removeItem(PERSISTENCE_KEY)
    window.localStorage.removeItem(HOME_SNAPSHOTS_PERSISTENCE_KEY)
  }
}

function createReverPersistence() {
  return createPersistedState({
    key: PERSISTENCE_KEY,
    paths: PERSISTED_PATHS,
    storage: createSafeStorage(),
    filter: mutation =>
      isPersistedMutation(PERSISTED_MUTATION_TYPES, mutation) &&
      !isOrgSwitchHandoffActive(),
  })
}

function createHomeSnapshotsPersistence() {
  return createPersistedState({
    key: HOME_SNAPSHOTS_PERSISTENCE_KEY,
    paths: HOME_SNAPSHOT_PERSISTED_PATHS,
    storage: createSafeStorage(),
    filter: mutation =>
      isPersistedMutation(HOME_SNAPSHOT_PERSISTED_MUTATION_TYPES, mutation) &&
      !isOrgSwitchHandoffActive(),
  })
}

export {
  createHomeSnapshotsPersistence,
  createReverPersistence,
  HOME_SNAPSHOTS_PERSISTENCE_KEY,
  HOME_SNAPSHOT_PERSISTED_MUTATION_TYPES,
  PERSISTED_MUTATION_TYPES,
  PERSISTED_PATHS,
  PERSISTENCE_KEY,
  removePersistedOrgSession
}
